/**
 * Route guards — one per half of the product.
 *
 * ── The four answers, and why none of them is a blank page ──────────────────
 *   checking      the quiet boot ground. NOT the login screen: showing the
 *                 login screen while the session probe is still in flight is
 *                 the flicker every signed-in person would see on every refresh.
 *   signed out    the login screen, remembering where they were going.
 *   unreachable   say that RocketRep did not answer, and offer a retry. Sending
 *                 somebody to sign in when the server is down is a loop they
 *                 cannot win.
 *   wrong role    a refusal that names who they are signed in as, which half
 *                 they asked for, and which half they may open — with a button
 *                 to it. A rep who taps a manager's link should land somewhere
 *                 useful, not on an empty screen.
 *
 * `mustChangePassword` diverts everything to the change screen. A password
 * somebody else issued is a password somebody else knows, so nothing behind it
 * opens until it is replaced.
 */

import type { ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { AuthShell, Booting, Message } from "./chrome";
import {
  AUDIENCE_NAME,
  AUDIENCE_TITLE,
  audiencesOf,
  initialsOf,
  pathForAudience,
  personAdmits,
  titleOf,
} from "./types";
import type { Audience, Person } from "./types";

export function RequireAudience({
  audience,
  children,
}: {
  audience: Audience;
  children: ReactNode;
}) {
  const { status, person, mustChangePassword, unreachableReason, recheck } = useAuth();
  const location = useLocation();

  if (status === "checking") return <Booting />;

  if (status === "unreachable") {
    return <Unreachable reason={unreachableReason} onRetry={recheck} />;
  }

  if (status === "signed-out" || !person) {
    // Carry the hash too: the PRO app routes on it, so a deep link to a doctor
    // survives the trip through the login screen.
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" state={{ from }} replace />;
  }

  if (mustChangePassword) return <Navigate to="/change-password" replace />;

  if (person.active === false) return <Deactivated person={person} />;

  // Asked of the PERSON, not the role: when the service states which halves it
  // will open for them, that statement is what the data routes enforce, and the
  // browser must not disagree with it.
  if (!personAdmits(person, audience)) {
    return <WrongAudience person={person} asked={audience} />;
  }

  return <>{children}</>;
}

/* ── the refusals ───────────────────────────────────────────────────────── */

function WrongAudience({ person, asked }: { person: Person; asked: Audience }) {
  const navigate = useNavigate();
  const allowed = audiencesOf(person);
  const { logout } = useAuth();

  return (
    <AuthShell
      wide
      eyebrow="RocketRep · Aspira"
      title={`${AUDIENCE_TITLE[asked]} is not yours to open`}
      sub={
        <>
          You are signed in and the account is fine — this half of RocketRep is for a
          different role, so it will not open for you.
        </>
      }
    >
      <Who person={person} />

      <Message heading={allowed.length ? "What you can open" : "Nothing is assigned to you"}>
        {allowed.length ? (
          <>
            A <b>{person.role === "PRO" ? "medical representative" : titleOf(person).toLowerCase()}</b>{" "}
            opens{" "}
            {allowed.map((a, i) => (
              <span key={a}>
                {i > 0 ? " and " : ""}
                <b>{AUDIENCE_NAME[a]}</b>
              </span>
            ))}
            .
          </>
        ) : (
          <>
            Your role opens neither half of RocketRep yet. That is an assignment your team
            leader can change — nothing is wrong with the account itself.
          </>
        )}
      </Message>

      <div className="rra-btns">
        {allowed.map((a) => (
          <button
            key={a}
            type="button"
            className="rra-cta-s"
            onClick={() => navigate(pathForAudience(person, a), { replace: true })}
          >
            Open {AUDIENCE_NAME[a]}
          </button>
        ))}
        <button type="button" className="rra-cta-s" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </AuthShell>
  );
}

function Deactivated({ person }: { person: Person }) {
  const { logout } = useAuth();
  return (
    <AuthShell
      wide
      eyebrow="RocketRep · Aspira"
      title="This account is switched off"
      sub="The sign-in worked, but the account has been deactivated. Nothing here will open until it is switched back on."
    >
      <Who person={person} />
      <Message heading="What to do">
        Ask your team leader to reactivate it. Your visits, notes and book are untouched —
        deactivating an account hides nothing and deletes nothing.
      </Message>
      <div className="rra-btns">
        <button type="button" className="rra-cta-s" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </AuthShell>
  );
}

function Unreachable({ reason, onRetry }: { reason: string | null; onRetry: () => void }) {
  return (
    <AuthShell
      wide
      eyebrow="RocketRep · Aspira"
      title="RocketRep did not answer"
      sub="You have not been signed out. RocketRep's own server could not be reached, which is a different thing and is worth keeping separate."
    >
      <Message tone="bad" heading="What happened">
        {reason ?? "The session check did not complete."}
      </Message>
      <Message heading="What it is not">
        This is not Aspira LaaS being down. RocketRep keeps its own accounts and its own
        server precisely so a deploy on the money platform cannot stop a rep signing in.
      </Message>
      <div className="rra-btns">
        <button type="button" className="rra-cta-s" onClick={onRetry}>
          Try again
        </button>
      </div>
    </AuthShell>
  );
}

/** Who you are signed in as — on every refusal, so nobody has to guess. */
function Who({ person }: { person: Person }) {
  return (
    <div className="rra-who">
      <span className="rra-av">{initialsOf(person)}</span>
      <span className="rra-usr-min">
        <span className="rra-who-n">{person.name}</span>
        <span className="rra-who-r">
          {titleOf(person)}
          {person.email ? ` · ${person.email}` : ""}
        </span>
      </span>
    </div>
  );
}
